import React, { useMemo } from 'react';

import cx from 'classnames';
import ReactPaginate from 'react-paginate';
import { useExpanded, useTable } from 'react-table';

import Icon from 'components/shared/Icon';
import Triangle from 'components/shared/Icon/icons/Triangle';
import Loader from 'components/shared/Loader';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

import styles from './index.module.css';
import useFixSizedColums from './useFixSizedColums';

const DEFAULT_LIMITS = [10, 25, 50, 100];

function Table({
  data,
  columns,
  limit = 10,
  limitsRange = DEFAULT_LIMITS,
  pagination = {},
  onSelectPage,
  onLimitChange,
  onClick,
  renderRowSubComponent,
  rowOptions,
  getHeaderProps = () => ({}),
  getCellProps = () => ({}),
  noDataText,
  withFixSizedColumns,
  isLoading = false,
  isStickyFirstColumn = false,
  tableWrapperClassName,
  tableClassName,
  theadClassName,
  tbodyClassName,
  tfootClassName,
  noPagination,
  autoResetExpanded = true,
}) {
  const memoizedData = useMemo(() => data, [data]);
  const memoizedColumns = useMemo(() => columns, [columns]);
  const tableInstance = useTable(
    {
      columns: memoizedColumns,
      data: memoizedData,
      autoResetExpanded,
    },
    useExpanded,
  );
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { currentPage, firstPage, lastPage, totalPages, totalCount } = pagination;
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = tableInstance;
  const { getFixSizedColumnStyles } = useFixSizedColums({ columns: memoizedColumns, enabled: withFixSizedColumns });
  const isNoData = rows?.length === 0;

  return (
    <div style={{ position: 'relative' }} onClick={onClick}>
      {isLoading && <Loader isFillBlock style={{ zIndex: '6' }} />}
      <div
        className={cx(
          styles.tableWrapper,
          rowOptions && styles.tableWrapperWithOptions,
          isStickyFirstColumn && isTabletOrMobile && styles.tableStickyFirstColumn,
          tableWrapperClassName,
        )}
      >
        <div className={styles.fitContent}>
          <table
            {...getTableProps()}
            className={cx(styles.table, tableClassName, { [styles.tableFixed]: withFixSizedColumns })}
          >
            <thead className={cx(styles.thead, theadClassName)}>
              {headerGroups.map((headerGroup, headerIdx) => (
                <tr key={headerIdx} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, i) => {
                    const { className: columnStyles, ...rest } = getHeaderProps(column);

                    return (
                      <th
                        key={i}
                        className={cx(
                          styles.cell,
                          styles.th,
                          columnStyles,
                          column.className,
                          column.getClassName && column.getClassName({ row: rows[headerIdx] }),
                          {
                            [styles.cellCenter]: column.center,
                          },
                        )}
                        {...column.getHeaderProps(rest)}
                        style={{ ...getFixSizedColumnStyles(column) }}
                        colSpan={column?.colSpan}
                      >
                        {column.render('Header')}
                      </th>
                    );
                  })}
                  {rowOptions && <th style={{ width: '1rem', padding: 0 }}></th>}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} className={cx(styles.tbody, tbodyClassName)}>
              {isNoData ? (
                <tr style={{ position: 'relative' }}>
                  <td className={cx(styles.cell, styles.cellTd, styles.noDataText)} colSpan={columns?.length}>
                    {noDataText ? noDataText : 'No Data'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  return (
                    <React.Fragment key={row.getRowProps().key}>
                      <tr {...row.getRowProps()} style={{ position: 'relative' }}>
                        {row.cells.map((cell, j) => {
                          const { className: cellStyles, ...rest } = getCellProps(cell);

                          return (
                            <td
                              key={j}
                              {...cell.getCellProps(rest)}
                              className={cx(styles.cell, styles.cellTd, cellStyles, {
                                [styles.cellCenter]: cell.column.center,
                              })}
                              style={{ ...getFixSizedColumnStyles(cell.column) }}
                              colSpan={cell?.column?.colSpan}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                        {rowOptions && rowOptions(row)}
                      </tr>

                      {row?.isExpanded ? renderRowSubComponent({ row }) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {!noPagination && totalCount > Math.min(...limitsRange) && (
              <tfoot className={tfootClassName}>
                <tr>
                  <td
                    className={cx(styles.cellPaginate, styles.cellTd, styles.cellPaginateFirst)}
                    colSpan={columns?.length}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ marginRight: '2rem' }}>
                        {totalPages > 1 && (
                          <span>
                            Page <strong>{currentPage}</strong> of {totalPages}
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <span>
                          Show{' '}
                          <select
                            className={styles.limitSelect}
                            onChange={(e) => onLimitChange(e.target.value)}
                            value={limit}
                          >
                            {limitsRange.map((l) => (
                              <option key={l} value={l}>
                                {l}
                              </option>
                            ))}
                          </select>{' '}
                          Records
                        </span>
                      </div>
                      <div>
                        {totalPages > 1 && (
                          <ReactPaginate
                            previousLabel={
                              <Icon
                                component={Triangle}
                                width="1rem"
                                height="1rem"
                                className={cx(styles.icon, styles.iconLeft, { [styles.iconDisabled]: firstPage })}
                                onClick={!firstPage ? () => onSelectPage(currentPage - 1) : () => null}
                              />
                            }
                            nextLabel={
                              <Icon
                                component={Triangle}
                                width="1rem"
                                height="1rem"
                                className={cx(styles.icon, styles.iconRight, { [styles.iconDisabled]: lastPage })}
                                onClick={!lastPage ? () => onSelectPage(currentPage + 1) : () => null}
                              />
                            }
                            forcePage={currentPage - 1}
                            onPageChange={({ selected }) => onSelectPage(selected + 1)}
                            breakLabel={'...'}
                            breakClassName={'break-me'}
                            pageCount={totalPages}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            containerClassName={styles.pagination}
                            subContainerClassName={'pages pagination'}
                            activeClassName={styles.paginationCountActive}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

export default React.memo(Table);
